/**
 * The item registry.
 *
 * One import and one entry per item, and nothing else. Every number about an item lives in the
 * item's own file, so this stays the list of what exists rather than a second place to tune
 * anything.
 *
 * `ITEM_ORDER` is what any listing walks, so a container rendered on two machines lists the same
 * things in the same sequence.
 */

import { BALLISTIC_ROUNDS } from '@/sim/items/items/ammo/ballisticRounds';
import { SHOTGUN_SHELLS } from '@/sim/items/items/ammo/shotgunShells';
import { COMBAT_HELMET } from '@/sim/items/items/armor/combatHelmet';
import { LUCKY_BOTTLE_CAP } from '@/sim/items/items/armor/luckyBottleCap';
import { MEDIC_POUCH } from '@/sim/items/items/armor/medicPouch';
import { PLATE_CARRIER } from '@/sim/items/items/armor/plateCarrier';
import { RADIATION_DOSIMETER } from '@/sim/items/items/armor/radiationDosimeter';
import { SCAVENGED_GREAVES } from '@/sim/items/items/armor/scavengedGreaves';
import { RECLAMATION_SCRIP } from '@/sim/items/items/currency/reclamationScrip';
import { FIELD_BANDAGE } from '@/sim/items/items/consumables/fieldBandage';
import { STIM_SHOT } from '@/sim/items/items/consumables/stimShot';
import { DUCT_TAPE_ROLL } from '@/sim/items/items/junk/ductTapeRoll';
import { REACTOR_PASTE } from '@/sim/items/items/junk/reactorPaste';
import { SALVAGED_GUN_PARTS } from '@/sim/items/items/junk/salvagedGunParts';
import { SCRAP_METAL } from '@/sim/items/items/junk/scrapMetal';
import { VACUUM_TUBES } from '@/sim/items/items/junk/vacuumTubes';
import { AK47_ITEM } from '@/sim/items/items/weapons/ak47';
import { BENELLI_M4_ITEM } from '@/sim/items/items/weapons/benelliM4';
import { GLOCK17_ITEM } from '@/sim/items/items/weapons/glock17';
import { M1911_ITEM } from '@/sim/items/items/weapons/m1911';
import { M249_ITEM } from '@/sim/items/items/weapons/m249';
import { M4A1_ITEM } from '@/sim/items/items/weapons/m4a1';
import { M82_ITEM } from '@/sim/items/items/weapons/m82';
import { MP5_ITEM } from '@/sim/items/items/weapons/mp5';
import { REMINGTON700_ITEM } from '@/sim/items/items/weapons/remington700';
import { REMINGTON870_ITEM } from '@/sim/items/items/weapons/remington870';
import { UZI_ITEM } from '@/sim/items/items/weapons/uzi';
import type { ItemDef, ItemId } from '@/sim/items/itemContract';

export const ITEMS = {
  glock17: GLOCK17_ITEM,
  m1911: M1911_ITEM,
  mp5: MP5_ITEM,
  uzi: UZI_ITEM,
  m4a1: M4A1_ITEM,
  ak47: AK47_ITEM,
  remington870: REMINGTON870_ITEM,
  benelliM4: BENELLI_M4_ITEM,
  remington700: REMINGTON700_ITEM,
  m82: M82_ITEM,
  m249: M249_ITEM,
  combatHelmet: COMBAT_HELMET,
  plateCarrier: PLATE_CARRIER,
  scavengedGreaves: SCAVENGED_GREAVES,
  radiationDosimeter: RADIATION_DOSIMETER,
  medicPouch: MEDIC_POUCH,
  luckyBottleCap: LUCKY_BOTTLE_CAP,
  fieldBandage: FIELD_BANDAGE,
  stimShot: STIM_SHOT,
  ballisticRounds: BALLISTIC_ROUNDS,
  shotgunShells: SHOTGUN_SHELLS,
  scrapMetal: SCRAP_METAL,
  ductTapeRoll: DUCT_TAPE_ROLL,
  salvagedGunParts: SALVAGED_GUN_PARTS,
  vacuumTubes: VACUUM_TUBES,
  reactorPaste: REACTOR_PASTE,
  reclamationScrip: RECLAMATION_SCRIP,
} as const satisfies Record<ItemId, ItemDef>;

/** Listing order, and part of the determinism contract. */
export const ITEM_ORDER: readonly ItemId[] = [
  'glock17',
  'm1911',
  'mp5',
  'uzi',
  'm4a1',
  'ak47',
  'remington870',
  'benelliM4',
  'remington700',
  'm82',
  'm249',
  'combatHelmet',
  'plateCarrier',
  'scavengedGreaves',
  'radiationDosimeter',
  'medicPouch',
  'luckyBottleCap',
  'fieldBandage',
  'stimShot',
  'ballisticRounds',
  'shotgunShells',
  'scrapMetal',
  'ductTapeRoll',
  'salvagedGunParts',
  'vacuumTubes',
  'reactorPaste',
  'reclamationScrip',
];

/** Def lookup, total over the union so it never widens to `undefined`. */
export function itemDef(id: ItemId): ItemDef {
  return ITEMS[id];
}

/**
 * An item's position in `ITEM_ORDER`, one based, so zero can mean no item at all.
 *
 * The same shape `weaponIndex` has and for the same reason: a fixed-field wire names a thing by a
 * number, and the order is already part of the determinism contract. Adding an item to the middle
 * of the order is a protocol change, which is what `PROTOCOL_VERSION` is for.
 */
export function itemIndex(id: ItemId): number {
  return ITEM_ORDER.indexOf(id) + 1;
}

/** The inverse. Null for 0 or an index past the end of `ITEM_ORDER`. */
export function itemByIndex(index: number): ItemId | null {
  return ITEM_ORDER[index - 1] ?? null;
}

/**
 * Whether `value` names an item. The gate every save and every wire message goes through, since
 * both carry an id as a string and neither can be trusted to still name something.
 */
export function isItemId(value: string): value is ItemId {
  return Object.hasOwn(ITEMS, value);
}
